import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { useDealInfo } from '../hooks/useDocumentCollection';
import DocumentUploadForm from '../components/document-upload/DocumentUploadForm';
import { UploadSuccess } from '../components/document-upload/UploadSuccess';
import DocumentUploadErrorBoundary from '../components/document-upload/ErrorBoundary';
import { brandColors } from '@horwath-htl/shared-branding';

/**
 * Company branding configuration
 */
interface CompanyBranding {
  primaryColor: string;
  headerBgColor: string;
  accentColor: string;
  companyName: string;
}

/**
 * Get company-specific branding based on managing company
 */
const getCompanyBranding = (managingCompany: string | null): CompanyBranding => {
  const company = managingCompany?.toLowerCase() || '';

  if (company.includes('barrett')) {
    return {
      primaryColor: '#1a365d', // Barrett blue
      headerBgColor: '#1a365d',
      accentColor: '#c9a227', // Barrett gold
      companyName: 'Barrett',
    };
  }

  // Default to Horwath HTL branding
  return {
    primaryColor: brandColors.blue,
    headerBgColor: brandColors.blue,
    accentColor: brandColors.gold,
    companyName: 'Horwath HTL',
  };
};

/**
 * Success data returned when form submission completes
 */
interface SuccessData {
  documentsCount: number;
  contactEmail: string;
}

/**
 * DocumentUploadPage - Page for external clients to upload documents for a deal
 *
 * URL Parameters:
 * - dealId (required): HubSpot Deal ID
 * - type (optional): Form variant type (default: 'standard')
 */
const DocumentUploadPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dealId = searchParams.get('dealId') || '';
  const formType = (searchParams.get('type') || 'standard') as 'standard' | 'retrospective';

  // Validate dealId is numeric (HubSpot Deal IDs are numeric)
  const isValidDealIdFormat = /^\d+$/.test(dealId);

  // Only fetch deal info if dealId format is valid
  const { dealInfo, loading, error } = useDealInfo(isValidDealIdFormat ? dealId : '');
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  const branding = getCompanyBranding(dealInfo?.managingCompany ?? null);

  /**
   * Handle successful form submission
   */
  const handleFormSuccess = (documentsCount: number, contactEmail: string) => {
    setSuccessData({ documentsCount, contactEmail });
  };

  // Render loading state
  if (loading) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 3,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading document upload form...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Render error state for missing or invalid dealId format
  if (!dealId || !isValidDealIdFormat) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {!dealId
              ? 'Please provide a deal ID in the URL'
              : 'Invalid deal ID format'}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            This page requires a valid numeric deal ID parameter. Please check the link you
            were provided and try again.
          </Typography>
        </Box>
      </Container>
    );
  }

  // Render error state for deal not found or API error
  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Unable to load the document upload form. Please contact support if this
            issue persists.
          </Typography>
        </Box>
      </Container>
    );
  }

  // Render success state after form completion
  if (successData && dealInfo) {
    return (
      <Container maxWidth="md">
        <UploadSuccess
          propertyName={dealInfo.propertyName}
          documentsCount={successData.documentsCount}
          contactEmail={successData.contactEmail}
          managingCompany={dealInfo.managingCompany || branding.companyName}
        />
      </Container>
    );
  }

  // Render form with deal info
  if (dealInfo) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          {/* Header with branding */}
          <Paper
            elevation={0}
            sx={{
              mb: 4,
              p: 4,
              backgroundColor: branding.headerBgColor,
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Accent stripe */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                backgroundColor: branding.accentColor,
              }}
            />

            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: '#FFFFFF',
                fontWeight: 600,
                mb: 1,
              }}
            >
              Document Upload
            </Typography>

            <Typography
              variant="h6"
              sx={{
                color: branding.accentColor,
                fontWeight: 500,
                mb: formType === 'retrospective' ? 1 : 2,
              }}
            >
              {dealInfo.propertyName}
            </Typography>

            {formType === 'retrospective' && (
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  mb: 2,
                  fontStyle: 'italic',
                }}
              >
                Retrospective Appraisal Form
              </Typography>
            )}

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
              }}
            >
              Managed by{' '}
              <Box
                component="span"
                sx={{
                  fontWeight: 600,
                  color: '#FFFFFF',
                }}
              >
                {dealInfo.managingCompany || branding.companyName}
              </Box>
            </Typography>
          </Paper>

          {/* Document Upload Form */}
          <DocumentUploadForm
            dealId={dealId}
            dealInfo={dealInfo}
            formType={formType}
            onSuccess={handleFormSuccess}
          />
        </Box>
      </Container>
    );
  }

  // Fallback - should not reach here
  return null;
};

/**
 * Wrapped component with error boundary for graceful error handling
 */
const DocumentUploadPageWithErrorBoundary: React.FC = () => (
  <DocumentUploadErrorBoundary>
    <DocumentUploadPage />
  </DocumentUploadErrorBoundary>
);

export default DocumentUploadPageWithErrorBoundary;
